import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import Modal from '../../common/Modal';
import { BTN_PRIMARY, BTN_SECONDARY } from '../../common/ui';
import { cameraTagStyle } from '../CameraTagChip';
import { Callout } from '../../common/Callout';
import { tagErrorMessage, type CameraTagCategory, type CameraTagsApi } from './cameraTagsApi';
import { useCameraTagCatalog, refreshCameraTagCatalog } from './useCameraTagCatalog';

type Change = 'add' | 'remove';

export interface BulkTagModalProps {
  api: CameraTagsApi;
  isOpen: boolean;
  onClose: () => void;
  cameraIds: string[];
  onApplied?: () => void;
  /** Where tags are managed — linked from the empty state when given. */
  manageTagsHref?: string;
}

/**
 * Add or remove tags on many cameras at once. Each tag cycles: no change → add → remove →
 * no change. Adding a tag from a one-per-camera category (Placement) replaces whatever those
 * cameras had there, so "set these twenty to Outdoor" works whatever they were before.
 */
export default function BulkTagModal({ api, isOpen, onClose, cameraIds, onApplied, manageTagsHref }: BulkTagModalProps) {
  const { catalog } = useCameraTagCatalog(api);
  const [changes, setChanges] = useState<Record<string, Change>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setChanges({}); setError(''); }
  }, [isOpen]);

  if (!isOpen) return null;

  const categories = catalog.categories.filter((c) => c.tags.length > 0);

  function cycle(category: CameraTagCategory, tagId: string) {
    setChanges((prev) => {
      const next = { ...prev };
      const now = prev[tagId];
      const to: Change | undefined = !now ? 'add' : now === 'add' ? 'remove' : undefined;
      if (to) next[tagId] = to; else delete next[tagId];
      // One-per-camera: only one tag of the category can be the one being added.
      if (to === 'add' && !category.multi_select) {
        for (const t of category.tags) if (t.id !== tagId && next[t.id] === 'add') delete next[t.id];
      }
      return next;
    });
  }

  const addIds = Object.keys(changes).filter((id) => changes[id] === 'add');
  const removeIds = Object.keys(changes).filter((id) => changes[id] === 'remove');

  async function apply() {
    setSaving(true);
    setError('');
    try {
      await api.bulkAssign(cameraIds, addIds, removeIds);
      refreshCameraTagCatalog(api);
      onApplied?.();
      onClose();
    } catch (err) {
      setError(tagErrorMessage(err, 'Could not update tags'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Tags"
      subtitle={`${cameraIds.length} camera${cameraIds.length === 1 ? '' : 's'} selected · click a tag to add it, again to remove it`}
      size="md"
      footer={(
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px] text-muted-foreground">
            {addIds.length || removeIds.length
              ? `${addIds.length ? `Add ${addIds.length}` : ''}${addIds.length && removeIds.length ? ', ' : ''}${removeIds.length ? `remove ${removeIds.length}` : ''}`
              : 'No changes yet'}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={BTN_SECONDARY}>
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={saving || (!addIds.length && !removeIds.length)}
              className={BTN_PRIMARY}
            >
              {saving ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>
      )}
    >
        <div className="space-y-4 px-5 py-4">
          {error && <Callout tone="danger">{error}</Callout>}

          {categories.length === 0 && (
            <p className="text-[13px] text-muted-foreground">
              No tags exist yet.{' '}
              {manageTagsHref && (
                <Link to={manageTagsHref} className="font-medium text-brand hover:underline">Create some in Settings</Link>
              )}
            </p>
          )}

          {categories.map((cat) => (
            <div key={cat.id}>
              <p className="mb-1.5 text-[12px] font-semibold text-foreground">
                {cat.name}
                {!cat.multi_select && <span className="ml-1.5 font-normal text-muted-foreground">one per camera — adding replaces</span>}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {cat.tags.map((t) => {
                  const change = changes[t.id];
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => cycle(cat, t.id)}
                      className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-[12px] font-medium transition-opacity hover:opacity-80 ${
                        change === 'remove' ? 'line-through' : ''
                      }`}
                      style={change === 'remove'
                        ? { color: 'var(--destructive)', borderColor: 'var(--destructive)' }
                        : cameraTagStyle(t.color, change === 'add')}
                    >
                      {change === 'add' && <PlusIcon className="h-3 w-3" />}
                      {change === 'remove' && <MinusIcon className="h-3 w-3" />}
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

    </Modal>
  );
}
